import React, { useState, useRef, useEffect, useMemo, useDeferredValue, useCallback } from 'react';
import { MdClose, MdSearch, MdExpandMore, MdCheck } from 'react-icons/md';
import './CustomDropdownWithSearch.css';

interface CustomDropdownWithSearchProps {
  value: string[];
  options: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  singleSelect?: boolean;
  showTags?: boolean;
}

const CustomDropdownWithSearch: React.FC<CustomDropdownWithSearchProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Select brands...',
  singleSelect = false,
  showTags = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(debouncedSearchTerm);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(300);
  const ITEM_HEIGHT = 36;
  const OVERSCAN = 6;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [searchTerm]);

  const normalizedOptions = useMemo(
    () => options.map(option => ({ label: option, lower: option.toLowerCase() })),
    [options]
  );
  const normalizedSearchTerm = deferredSearchTerm.trim().toLowerCase();

  // Filter options based on deferred search term to keep typing responsive
  const filteredOptions = useMemo(() => {
    if (!normalizedSearchTerm) {
      return options;
    }
    return normalizedOptions
      .filter(option => option.lower.includes(normalizedSearchTerm))
      .map(option => option.label);
  }, [normalizedOptions, normalizedSearchTerm, options]);

  useEffect(() => {
    setScrollTop(0);
    if (optionsListRef.current) {
      optionsListRef.current.scrollTop = 0;
    }
  }, [normalizedSearchTerm]);

  const totalHeight = filteredOptions.length * ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    filteredOptions.length,
    Math.ceil((scrollTop + viewportHeight) / ITEM_HEIGHT) + OVERSCAN
  );
  const visibleOptions = filteredOptions.slice(startIndex, endIndex);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const nextScrollTop = event.currentTarget.scrollTop;
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      setScrollTop(nextScrollTop);
      scrollRafRef.current = null;
    });
  }, []);

  // Handle checkbox change
  const handleOptionChange = (option: string) => {
    if (singleSelect) {
      onChange([option]);
      setIsOpen(false);
    } else {
      if (value.includes(option)) {
        onChange(value.filter(item => item !== option));
      } else {
        onChange([...value, option]);
      }
    }
  };

  // Handle select all / deselect all
  const allSelected = filteredOptions.length > 0 && filteredOptions.every(opt => value.includes(opt));

  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all filtered options
      onChange(value.filter(item => !filteredOptions.includes(item)));
    } else {
      // Select all filtered options (merge with existing)
      const merged = new Set([...value, ...filteredOptions]);
      onChange(Array.from(merged));
    }
  };

  // Handle tag removal
  const handleRemoveTag = (option: string) => {
    onChange(value.filter(item => item !== option));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      const updateViewportHeight = () => {
        if (optionsListRef.current) {
          setViewportHeight(optionsListRef.current.clientHeight || 300);
        }
      };
      updateViewportHeight();
      window.addEventListener('resize', updateViewportHeight);
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        window.removeEventListener('resize', updateViewportHeight);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  return (
    <div className="custom-dropdown-search-wrapper" ref={dropdownRef}>
      {/* Display Selected Items - Clickable Trigger */}
      <button
        className="selected-items-container"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        {value.length > 0 ? (
          <div className="selected-summary">
            <span className="summary-text">
              {singleSelect ? value[0] : (value.length === options.length ? placeholder : `${value.length} selected`)}
            </span>
            <MdExpandMore size={18} className={`chevron-icon ${isOpen ? 'open' : ''}`} />
          </div>
        ) : (
          <div className="selected-summary">
            <span className="summary-text">{placeholder}</span>
            <MdExpandMore size={18} className={`chevron-icon ${isOpen ? 'open' : ''}`} />
          </div>
        )}
      </button>

      {/* Selected Tags - only show in multi-select mode when showTags is true */}
      {showTags && !singleSelect && value.length > 0 && (
        <div className="selected-tags">
          {value.map(item => (
            <div key={item} className="tag">
              <span className="tag-text">{item}</span>
              <button
                className="tag-remove"
                onClick={() => handleRemoveTag(item)}
                type="button"
              >
                <MdClose size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="custom-dropdown-search-menu">
          {/* Search Input */}
          <div className="retailer-search-input-wrapper">
            <MdSearch size={16} className="retailer-search-icon" />
            <input
              type="text"
              placeholder={`Search ${placeholder.replace(/^All\s*/i, '').toLowerCase()}...`}
              className="retailer-dropdown-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          {/* Select All - only show in multi-select mode */}
          {!singleSelect && filteredOptions.length > 0 && (
            <div className="all-brands-header">
              <label className="all-brands-label select-all-label">
                <input
                  type="checkbox"
                  className="option-checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
                {allSelected && <MdCheck size={18} className="checkmark" />}
              </label>
            </div>
          )}

          {/* Options List */}
          <div className="options-list" ref={optionsListRef} onScroll={handleScroll}>
            {filteredOptions.length > 0 ? (
              <div className="options-list-inner" style={{ height: totalHeight }}>
                {visibleOptions.map((option, index) => {
                  const optionIndex = startIndex + index;
                  const itemStyle: React.CSSProperties = {
                    top: optionIndex * ITEM_HEIGHT,
                    height: ITEM_HEIGHT
                  };

                  return singleSelect ? (
                    <button
                      key={option}
                      className={`custom-dropdown-item virtualized-item ${value.includes(option) ? 'active' : ''}`}
                      onClick={() => handleOptionChange(option)}
                      type="button"
                      style={itemStyle}
                    >
                      <span className="custom-dropdown-item-label">{option}</span>
                      {value.includes(option) && (
                        <MdCheck size={18} className="dropdown-check-icon" />
                      )}
                    </button>
                  ) : (
                    <label
                      key={option}
                      className="option-item virtualized-item"
                      style={itemStyle}
                    >
                      <input
                        type="checkbox"
                        className="option-checkbox"
                        checked={value.includes(option)}
                        onChange={() => handleOptionChange(option)}
                      />
                      <span className="option-label">{option}</span>
                      {value.includes(option) && (
                        <MdCheck size={18} className="checkmark" />
                      )}
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="no-results">No {placeholder.replace(/^All\s*/i, '').toLowerCase()} found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDropdownWithSearch;
